import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Table, Space, Tooltip, Popconfirm, Card, Row, Col, Input, message, Badge, Modal, Typography, Alert, Select, Statistic, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, SearchOutlined, ClearOutlined, ExclamationCircleOutlined, ReloadOutlined, DownloadOutlined } from '@ant-design/icons';
import { customersService, type Customer as ApiCustomer } from '../services/customersService';
import { useAuth } from '../contexts/AuthContext';
import type { ColumnsType } from 'antd/es/table';
import UserAvatar from '../components/Common/UserAvatar';
import { exportToCSV } from '../utils/exportUtils';

const { Text } = Typography;
const { Option } = Select;

// Type pour afficher les clients dans le Table
interface CustomerRow {
  id: number;
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  phone: string;
  company: string;
  city: string;
  commune: string;
  status: string;
  lastVisit: string;
  latitude: number | null;
  longitude: number | null;
  profileImage?: string | null;
}

const CustomersPage: React.FC = (): React.ReactElement => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';
  const [loading, setLoading] = useState<boolean>(false);
  const [customers, setCustomers] = useState<ApiCustomer[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [purgingDeleted, setPurgingDeleted] = useState<boolean>(false);
  const [deletedCustomersCount, setDeletedCustomersCount] = useState<number>(0);
  
  // États pour la modal de confirmation de suppression
  const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);
  const [customerToDelete, setCustomerToDelete] = useState<{ id: number; name: string } | null>(null);
  const [confirmationCode, setConfirmationCode] = useState<string>('');
  const [enteredCode, setEnteredCode] = useState<string>('');
  const [deleting, setDeleting] = useState<boolean>(false);
  
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Fonction pour charger les clients
  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const data = await customersService.getCustomers({
        page: pagination.current,
        limit: pagination.pageSize,
        search: searchTerm,
        status: statusFilter || undefined,
      });
      
      setCustomers(data.data.customers || []);
      setPagination(prev => ({
        ...prev,
        total: data.data.total || 0,
      }));

      // Récupérer le nombre total global de clients supprimés en attente de purge
      const deletedCount = data.data.deleted_count !== undefined 
        ? data.data.deleted_count 
        : (data.data.customers || []).filter(
            (c: ApiCustomer) => 
              (c.email && c.email.startsWith('deleted_')) ||
              (c.phone && c.phone.startsWith('deleted_')) ||
              (c.status === 'deleted') ||
              (c.user?.status === 'deleted')
          ).length;
      setDeletedCustomersCount(deletedCount);
    } catch (error) {
      console.error('Erreur lors du chargement des clients:', error);
      message.error('Erreur lors du chargement des clients');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, [pagination.current, pagination.pageSize, searchTerm, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddCustomer = () => {
    navigate('/customers/new');
  };

  const handleEditCustomer = (id: number) => {
    navigate(`/customers/edit/${id}`);
  };

  // Générer un code de confirmation aléatoire à 6 chiffres
  const generateConfirmationCode = (): string => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  // Ouvrir la modal de confirmation de suppression
  const openDeleteModal = (id: number, name: string) => {
    const code = generateConfirmationCode();
    setCustomerToDelete({ id, name });
    setConfirmationCode(code);
    setEnteredCode('');
    setDeleteModalVisible(true);
  };

  // Fermer la modal
  const closeDeleteModal = () => {
    setDeleteModalVisible(false);
    setCustomerToDelete(null);
    setConfirmationCode('');
    setEnteredCode('');
  };

  // Confirmer la suppression avec vérification du code
  const handleConfirmDelete = async () => {
    if (enteredCode !== confirmationCode) {
      message.error('Code de confirmation incorrect');
      return;
    }

    if (!customerToDelete) return;

    try {
      setDeleting(true);
      await customersService.deleteCustomer(customerToDelete.id);
      message.success('Client supprimé avec succès');
      closeDeleteModal();
      fetchCustomers();
    } catch (error) {
      console.error('Erreur lors de la suppression du client:', error);
      message.error('Suppression échouée');
    } finally {
      setDeleting(false);
    }
  };

  const handleViewCustomer = (id: number) => {
    navigate(`/customers/${id}`);
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleTypeFilter = (value: string) => {
    setTypeFilter(value || '');
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleStatusFilter = (value: string) => {
    setStatusFilter(value || '');
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setTypeFilter('');
    setStatusFilter('');
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handlePurgeDeletedCustomers = async () => {
    try {
      setPurgingDeleted(true);
      const response = await customersService.purgeDeletedCustomers();
      
      if (response.success) {
        message.success(response.message || `${response.successCount} client(s) supprimé(s) définitivement`);
        fetchCustomers(); // Recharger la liste
      } else {
        message.error(response.message || 'Erreur lors de la purge');
      }
    } catch (error: any) {
      console.error('Erreur lors de la purge:', error);
      message.error(error.response?.data?.message || 'Erreur lors de la purge des clients supprimés');
    } finally {
      setPurgingDeleted(false);
    }
  };

  // Statistiques
  const stats = [
    { 
      title: 'Total Clients', 
      value: pagination.total,
      color: '#1890ff'
    },
    { 
      title: 'Actifs', 
      value: customers.filter(c => (c.status || c.user?.status || 'active') === 'active').length,
      color: '#52c41a'
    },
    { 
      title: 'Entreprises', 
      value: customers.filter(c => !!(c.company || c.company_name) && c.company !== 'N/A' && c.company_name !== 'N/A').length,
      color: '#722ed1'
    },
    { 
      title: 'Particuliers', 
      value: customers.filter(c => (!c.company && !c.company_name) || c.company === 'N/A' || c.company_name === 'N/A').length,
      color: '#fa8c16'
    },
  ];

  // Colonnes du tableau
  const columns: ColumnsType<CustomerRow> = [
    {
      title: 'Avatar',
      key: 'avatar',
      width: 70,
      align: 'center',
      render: (_, record) => (
        <UserAvatar
          src={record.profileImage}
          firstName={record.firstName}
          lastName={record.lastName}
          size={40}
        />
      ),
    },
    {
      title: 'Nom',
      key: 'name',
      render: (_, record) => (
        <div>
          <Button 
            type="link" 
            onClick={() => handleViewCustomer(record.id)}
            style={{ padding: 0, fontWeight: 600, fontSize: '14px', textAlign: 'left', height: 'auto' }}
          >
            {record.name}
          </Button>
          {record.company && (
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: 2 }}>
              🏢 {record.company}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (_, record) => (
        <div>
          {record.phone && record.phone !== 'N/A' && (
            <div style={{ fontWeight: 500, color: '#111827' }}>
              📱 {record.phone}
            </div>
          )}
          {record.email && record.email !== 'N/A' && (
            <div style={{ color: '#6b7280', fontSize: '12px', marginTop: 2 }}>
              ✉️ {record.email}
            </div>
          )}
          {(!record.phone || record.phone === 'N/A') && (!record.email || record.email === 'N/A') && (
            <span style={{ color: '#9ca3af' }}>N/A</span>
          )}
        </div>
      ),
    },
    {
      title: 'Localisation',
      key: 'location',
      render: (_, record) => {
        const parts = [record.commune, record.city].filter(p => p && p !== 'N/A');
        if (parts.length === 0) return <span style={{ color: '#9ca3af' }}>N/A</span>;
        return (
          <div>
            <span style={{ fontWeight: 500 }}>📍 {parts.join(', ')}</span>
          </div>
        );
      },
    },
    {
      title: 'Type',
      key: 'type',
      align: 'center',
      render: (_, record) => {
        const isCompany = !!record.company;
        return (
          <Tag color={isCompany ? 'purple' : 'cyan'}>
            {isCompany ? 'Entreprise' : 'Particulier'}
          </Tag>
        );
      },
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      render: (status: string) => {
        const statusConfig: Record<string, { color: string; text: string }> = {
          active: { color: 'green', text: 'Actif' },
          inactive: { color: 'red', text: 'Inactif' },
          pending: { color: 'orange', text: 'En attente' },
          deleted: { color: 'default', text: 'Supprimé' },
        };
        const config = statusConfig[status] || { color: 'green', text: 'Actif' };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: 'Dernière mise à jour',
      dataIndex: 'lastVisit',
      key: 'lastVisit',
      render: (text: string) => {
        if (!text) return <span style={{ color: '#9ca3af' }}>N/A</span>;
        const d = new Date(text);
        return isNaN(d.getTime()) ? text : d.toLocaleDateString('fr-FR');
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 140,
      align: 'center',
      render: (_: any, record: CustomerRow) => (
        <Space size="small">
          <Tooltip title="Voir les détails">
            <Button 
              type="text" 
              icon={<EyeOutlined />} 
              size="small"
              onClick={() => handleViewCustomer(record.id)} 
            />
          </Tooltip>
          <Tooltip title="Modifier">
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              size="small"
              onClick={() => handleEditCustomer(record.id)} 
            />
          </Tooltip>
          {isAdmin && (
            <Tooltip title="Supprimer">
              <Button 
                type="text" 
                icon={<DeleteOutlined />} 
                size="small"
                danger 
                onClick={() => openDeleteModal(record.id, record.name)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  // Transformer les données des clients pour le tableau
  const transformCustomersToRows = (rawCustomers: ApiCustomer[]): CustomerRow[] => {
    return rawCustomers
      .filter(customer => {
        const isCompany = !!(customer.company || customer.company_name) && (customer.company !== 'N/A' && customer.company_name !== 'N/A');
        if (typeFilter === 'company' && !isCompany) return false;
        if (typeFilter === 'individual' && isCompany) return false;

        const status = customer.status || customer.user?.status || 'active';
        if (statusFilter) {
          if (status !== statusFilter) return false;
        } else {
          // Par défaut, ne jamais afficher les comptes supprimés dans la vue principale
          if (status === 'deleted') return false;
        }

        return true;
      })
      .map(customer => {
        const firstName = customer.first_name || '';
        const lastName = customer.last_name || '';
        const fullName = `${firstName} ${lastName}`.trim() || 'N/A';
        const company = customer.company || customer.company_name || '';
        const status = customer.status || customer.user?.status || 'active';

        return {
          id: customer.id,
          firstName,
          lastName,
          name: fullName,
          email: customer.user?.email || customer.email || '',
          phone: customer.phone || customer.user?.phone || '',
          company: company && company !== 'N/A' ? company : '',
          city: customer.city || '',
          commune: customer.commune || '',
          status,
          lastVisit: customer.updated_at || customer.created_at || customer.updatedAt || '',
          latitude: customer.latitude ?? null,
          longitude: customer.longitude ?? null,
          profileImage: (customer as any).profile_image || (customer.user as any)?.profile_image || null,
        };
      });
  };

  const [exporting, setExporting] = useState<boolean>(false);

  const handleExport = () => {
    try {
      setExporting(true);
      const rows = customerRows.map(c => ({
        id: c.id,
        prenom: c.firstName,
        nom: c.lastName,
        entreprise: c.company || 'Particulier',
        email: c.email || '',
        telephone: c.phone || '',
        ville: c.city || '',
        commune: c.commune || '',
        statut: c.status === 'active' ? 'Actif' : c.status === 'inactive' ? 'Inactif' : c.status,
        date_mise_a_jour: c.lastVisit ? new Date(c.lastVisit).toLocaleDateString('fr-FR') : ''
      }));

      exportToCSV(
        rows,
        [
          { header: 'ID', key: 'id' },
          { header: 'Prénom', key: 'prenom' },
          { header: 'Nom', key: 'nom' },
          { header: 'Entreprise', key: 'entreprise' },
          { header: 'Email', key: 'email' },
          { header: 'Téléphone', key: 'telephone' },
          { header: 'Ville', key: 'ville' },
          { header: 'Commune', key: 'commune' },
          { header: 'Statut', key: 'statut' },
          { header: 'Dernière mise à jour', key: 'date_mise_a_jour' },
        ],
        `clients_${new Date().toISOString().slice(0, 10)}.csv`
      );
      message.success('Export des clients généré avec succès');
    } catch (err) {
      console.error('Erreur export clients:', err);
      message.error("Erreur lors de l'export des clients");
    } finally {
      setExporting(false);
    }
  };

  const customerRows = transformCustomersToRows(customers);

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* En-tête */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '24px',
        backgroundColor: '#fff',
        padding: '16px 24px',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 600 }}>Gestion des Clients</h1>
          <p style={{ margin: '4px 0 0 0', color: '#666' }}>
            Gérez vos clients et leurs informations
          </p>
        </div>
        <Space>
          {isAdmin && deletedCustomersCount > 0 && (
            <Popconfirm
              title="Purger les clients supprimés?"
              description={`${deletedCustomersCount} client(s) marqué(s) comme supprimé(s) seront définitivement effacés de la base de données.`}
              onConfirm={handlePurgeDeletedCustomers}
              okText="Purger"
              cancelText="Annuler"
              okButtonProps={{ danger: true }}
            >
              <Badge count={deletedCustomersCount} offset={[-5, 5]}>
                <Button 
                  danger
                  icon={<ClearOutlined />} 
                  size="large"
                  loading={purgingDeleted}
                  style={{ height: '40px' }}
                >
                  Purger supprimés
                </Button>
              </Badge>
            </Popconfirm>
          )}
          <Button 
            icon={<DownloadOutlined />} 
            size="large"
            onClick={handleExport}
            loading={exporting}
            style={{ height: '40px' }}
          >
            Exporter
          </Button>
          <Button 
            icon={<ReloadOutlined />} 
            size="large"
            onClick={fetchCustomers}
            loading={loading}
            style={{ height: '40px' }}
          >
            Actualiser
          </Button>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            size="large"
            onClick={handleAddCustomer}
            style={{ height: '40px' }}
          >
            Nouveau Client
          </Button>
        </Space>
      </div>

      {/* Statistiques */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        {stats.map((stat, index) => (
          <Col xs={24} sm={12} md={6} key={index}>
            <Card size="small" style={{ borderRadius: '8px' }}>
              <Statistic
                title={stat.title}
                value={stat.value}
                valueStyle={{ 
                  color: stat.color,
                  fontSize: '24px',
                  fontWeight: 600
                }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* Filtres */}
      <Card style={{ marginBottom: '16px', borderRadius: '8px' }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <Input
              placeholder="Rechercher par nom, email ou entreprise..."
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              allowClear
              size="large"
            />
          </Col>
          <Col xs={24} md={6}>
            <Select
              placeholder="Filtrer par type"
              style={{ width: '100%' }}
              size="large"
              allowClear
              value={typeFilter || undefined}
              onChange={handleTypeFilter}
            >
              <Option value="individual">Particulier</Option>
              <Option value="company">Entreprise</Option>
            </Select>
          </Col>
          <Col xs={24} md={6}>
            <Select
              placeholder="Filtrer par statut"
              style={{ width: '100%' }}
              size="large"
              allowClear
              value={statusFilter || undefined}
              onChange={handleStatusFilter}
            >
              <Option value="active">Actif</Option>
              <Option value="inactive">Inactif</Option>
              <Option value="pending">En attente</Option>
              <Option value="deleted">Supprimé</Option>
            </Select>
          </Col>
          <Col xs={24} md={4}>
            <Button 
              onClick={handleResetFilters}
              size="large"
              style={{ width: '100%', height: '40px' }}
            >
              Réinitialiser
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Tableau */}
      <Card style={{ borderRadius: '8px' }}>
        <Table
          columns={columns}
          dataSource={customerRows}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} sur ${total} clients`,
            onChange: (page, pageSize) => {
              setPagination(prev => ({
                ...prev,
                current: page,
                pageSize: pageSize || 10,
              }));
            },
          }}
          scroll={{ x: 900 }}
        />
      </Card>

      {/* Modal de confirmation de suppression avec code */}
      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#ff4d4f', fontSize: '20px' }} />
            <span>Confirmation de suppression</span>
          </Space>
        }
        open={deleteModalVisible}
        onCancel={closeDeleteModal}
        footer={[
          <Button key="cancel" onClick={closeDeleteModal} disabled={deleting}>
            Annuler
          </Button>,
          <Button
            key="delete"
            type="primary"
            danger
            onClick={handleConfirmDelete}
            loading={deleting}
            disabled={enteredCode.length !== 6}
          >
            Confirmer la suppression
          </Button>,
        ]}
        width={500}
      >
        <div style={{ padding: '16px 0' }}>
          <Alert
            message="⚠️ Action irréversible"
            description={
              <>
                Vous êtes sur le point de supprimer définitivement le client{' '}
                <Text strong>"{customerToDelete?.name}"</Text> et toutes ses données associées
                (commandes, interventions, devis, etc.).
              </>
            }
            type="warning"
            showIcon
            style={{ marginBottom: '24px' }}
          />

          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <Text type="secondary">Pour confirmer, entrez le code suivant :</Text>
            <div
              style={{
                fontSize: '32px',
                fontWeight: 'bold',
                letterSpacing: '8px',
                color: '#ff4d4f',
                backgroundColor: '#fff2f0',
                padding: '16px 24px',
                borderRadius: '8px',
                margin: '16px 0',
                fontFamily: 'monospace',
              }}
            >
              {confirmationCode}
            </div>
          </div>

          <Input
            placeholder="Entrez le code de confirmation"
            value={enteredCode}
            onChange={(e) => setEnteredCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            maxLength={6}
            size="large"
            style={{
              textAlign: 'center',
              fontSize: '24px',
              letterSpacing: '8px',
              fontFamily: 'monospace',
            }}
            status={enteredCode.length === 6 && enteredCode !== confirmationCode ? 'error' : undefined}
          />
          {enteredCode.length === 6 && enteredCode !== confirmationCode && (
            <Text type="danger" style={{ display: 'block', textAlign: 'center', marginTop: '8px' }}>
              Code incorrect
            </Text>
          )}
          {enteredCode.length === 6 && enteredCode === confirmationCode && (
            <Text type="success" style={{ display: 'block', textAlign: 'center', marginTop: '8px' }}>
              ✓ Code correct
            </Text>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default CustomersPage;